import { COOKIE_API_TOKEN, COOKIE_SESSION } from '@/constants/cookies';
import { camelize } from 'inflected';
import { ApolloClient } from 'apollo-client';
import { HttpLink } from 'apollo-link-http';
import { ApolloLink, concat } from 'apollo-link';
import { InMemoryCache } from 'apollo-cache-inmemory';
import isArray from 'lodash/isArray';
import isPlainObject from 'lodash/isPlainObject';

export default (context, inject) => {
  const tokenInfo = context.store.getters['utils/getApiTokens'] || context.$cookies.get(COOKIE_API_TOKEN);
  const token = tokenInfo ? tokenInfo.access_token : null;
  const sessionCookie = context.$cookies.get(COOKIE_SESSION);

  const uri = `${context.env.NUXT_ENV_CAPYBARA ? 'http://localhost:5002' : context.$config.websocketHost}/graphql`;
  const httpLink = new HttpLink({ uri });
  const headers = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (sessionCookie) {
    headers['Cookie'] = `_bonkers_session=${sessionCookie}`;
  }

  const stripNodes = (obj) => {
    if (isArray(obj)) {
      return obj.map(el => stripNodes(el));
    }
    if (!isPlainObject(obj)) {
      return obj;
    }
    const result = {};
    Object.keys(obj).forEach(key => {
      if (!key.startsWith('__')) {
        result[key] = stripNodes(obj[key]);
      }
    });
    return result;
  };

  const formatResponse = (response) => stripNodes(response.data[Object.keys(response.data)[0]]);

  const authMiddleware = new ApolloLink((operation, forward) => {
    operation.setContext({ headers });
    return forward(operation);
  });

  const apollo = new ApolloClient({ cache: new InMemoryCache(), link: concat(authMiddleware, httpLink) });

  const apolloMutate = async (mutation, input) => {
    return apollo.mutate({ mutation, variables: { input: deepCamelize(input) } })
      .then(r => {
        const data = formatResponse(r, context.store);
        if (!data.success) {
          context.$honeybadgerGeneric.addBreadcrumb('Apollo Mutation Error', {
            category: 'request', metadata: { mutation, input, data }
          });
          Promise.reject(data.errors);
        }
        delete data.errors;
        delete data.success;
        const keyName = Object.keys(data)[0];
        return Promise.resolve(data[keyName]);
      })
      .catch(e => {
        console.log(e);
        context.$honeybadgerGeneric.addBreadcrumb('Apollo Mutation Error', {
          category: 'request', metadata: { mutation, input }
        });
        return Promise.reject(e);
      });
  };

  const apolloQuery = async (query, variables) => {
    return apollo.query({ query, variables })
      .then(r => Promise.resolve(formatResponse(r, context.store)))
      .catch(e => {
        context.$honeybadgerGeneric.addBreadcrumb('Apollo Query Error', {
          category: 'request', metadata: { query, variables }
        });
        return Promise.reject(e);
      });
  };

  inject('apolloMutate', apolloMutate);
  inject('apolloQuery', apolloQuery);
  context.$apolloMutate = apolloMutate;
  context.$apolloQuery = apolloQuery;
};

const deepCamelize = (obj) => {
  if (!isPlainObject(obj)) {
    return obj;
  }
  const result = {};
  Object.keys(obj).forEach(key => {
    const val = obj[key];
    result[camelize(key, false)] = isPlainObject(val) ? deepCamelize(val) : val;
  });
  return result;
};
